import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Card, { SectionTitle } from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import type { Cycle, Enrollment, SchoolClass } from "@/types";
import { isInClass } from "@/utils/enrollment";
import { plural } from "@/utils/format";

interface Props {
  cycles: Cycle[];
  classes: SchoolClass[];
  enrollments: Enrollment[];
  schoolYear: string;
}

interface Row {
  name: string;
  count: number;
  /** Places libres (barre claire derrière l'effectif) ; 0 sans capacité renseignée. */
  free: number;
  /** Capacité de la classe (null : non renseignée dans Classes). */
  capacity: number | null;
  /** Remplissage en % (null sans capacité). */
  rate: number | null;
}

/** Couleur selon le remplissage : bleu, orange à 80 %, rouge quand la classe est pleine (bleu sans capacité). */
const fillOf = (rate: number | null) =>
  rate !== null && rate >= 100
    ? "oklch(var(--accent-500))"
    : rate !== null && rate >= 80
      ? "oklch(var(--warning-500))"
      : "oklch(var(--primary-500))";

const LEGEND = [
  { label: "Places disponibles", color: "oklch(var(--primary-500))" },
  { label: "Presque pleine (80 %)", color: "oklch(var(--warning-500))" },
  { label: "Pleine", color: "oklch(var(--accent-500))" },
];

function RowTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: Row }> }) {
  const row = active ? payload?.[0]?.payload : undefined;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-xs shadow-raised">
      <p className="font-semibold text-foreground-950">{row.name}</p>
      <p className="mt-0.5 text-foreground-600">
        {plural(row.count, "élève")}
        {row.capacity !== null ? ` sur ${row.capacity} places` : ""}
      </p>
      {row.capacity === null || row.rate === null ? (
        <p className="text-foreground-500">Capacité non renseignée (page Classes)</p>
      ) : (
        <p className="font-medium" style={{ color: fillOf(row.rate) }}>
          {row.rate >= 100 ? "Classe pleine" : `Remplie à ${row.rate} % · ${plural(row.free, "place libre", "places libres")}`}
        </p>
      )}
    </div>
  );
}

/** Effectifs de l'année par classe, un graphique par cycle, comparés à la capacité de chaque classe. */
export default function ClassDistribution({ cycles, classes, enrollments, schoolYear }: Props) {
  const current = enrollments.filter((e) => e.schoolYear === schoolYear && isInClass(e.status));
  const withCapacity = classes.some((c) => (c.capacity ?? 0) > 0);

  return (
    <Card>
      <SectionTitle
        icon="ri-bar-chart-horizontal-line"
        title="Effectifs par classe"
        subtitle={`Année ${schoolYear} · ${withCapacity ? "élèves inscrits et places" : "élèves inscrits"}`}
      />
      {withCapacity ? (
        <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1.5">
          {LEGEND.map((l) => (
            <span key={l.label} className="inline-flex items-center gap-1.5 text-xs text-foreground-600">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: l.color }} aria-hidden="true"></span>
              {l.label}
            </span>
          ))}
        </div>
      ) : (
        <p className="mb-4 text-xs text-foreground-500">
          Renseignez la capacité des classes (page Classes) pour voir leur remplissage.
        </p>
      )}
      <div className="space-y-6">
        {cycles.map((cycle) => {
          const rows: Row[] = classes
            .filter((c) => c.cycleId === cycle.id)
            .sort((a, b) => (a.level ?? 99) - (b.level ?? 99) || a.name.localeCompare(b.name))
            .map((cls) => {
              const count = current.filter((e) => e.classId === cls.id).length;
              const capacity = cls.capacity && cls.capacity > 0 ? cls.capacity : null;
              return {
                name: cls.name,
                count,
                capacity,
                free: capacity !== null ? Math.max(0, capacity - count) : 0,
                rate: capacity !== null ? Math.round((count / capacity) * 100) : null,
              };
            });
          const total = rows.reduce((sum, r) => sum + r.count, 0);
          // Remplissage du cycle seulement si toutes ses classes ont une capacité.
          const places = rows.every((r) => r.capacity !== null) ? rows.reduce((sum, r) => sum + (r.capacity ?? 0), 0) : 0;
          return (
            <div key={cycle.id}>
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                    <i className={`${cycle.code === "PRIMAIRE" ? "ri-seedling-line" : "ri-book-read-line"} text-sm`}></i>
                  </span>
                  <span className="text-sm font-semibold text-foreground-900">{cycle.label}</span>
                </div>
                <Badge tone="secondary">
                  {plural(total, "élève")}
                  {places ? ` · ${Math.round((total / places) * 100)} % des places` : ""}
                </Badge>
              </div>
              {rows.length === 0 ? (
                <p className="rounded-md border border-dashed border-background-300 py-4 text-center text-xs text-foreground-500">
                  Aucune classe dans ce cycle.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={rows.length * 36 + 8}>
                  <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 72, left: 0, bottom: 4 }} barCategoryGap={8}>
                    <XAxis type="number" hide domain={[0, "dataMax"]} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={96}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "oklch(var(--foreground-700))" }}
                    />
                    <Tooltip content={<RowTooltip />} cursor={{ fill: "oklch(var(--background-100))" }} />
                    <Bar dataKey="count" stackId="places" radius={[6, 0, 0, 6]} maxBarSize={22} isAnimationActive animationDuration={600}>
                      {rows.map((r) => (
                        <Cell key={r.name} fill={fillOf(r.rate)} />
                      ))}
                    </Bar>
                    <Bar dataKey="free" stackId="places" fill="oklch(var(--background-200))" radius={[0, 6, 6, 0]} maxBarSize={22} isAnimationActive={false}>
                      <LabelList
                        dataKey="count"
                        position="right"
                        content={(props) => {
                          const { x = 0, y = 0, width = 0, height = 0, index = 0 } = props as {
                            x?: number;
                            y?: number;
                            width?: number;
                            height?: number;
                            index?: number;
                          };
                          const r = rows[index];
                          return (
                            <text x={Number(x) + Number(width) + 8} y={Number(y) + Number(height) / 2 + 4} fontSize={12} fill="oklch(var(--foreground-600))">
                              <tspan fontWeight={600} fill="oklch(var(--foreground-900))">{r.count}</tspan>
                              {r.capacity !== null ? `/${r.capacity}` : r.count > 1 ? " élèves" : " élève"}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
